import React, { ChangeEvent, useState, useRef } from 'react';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { TextField, Button, List, ListItem, Typography } from '@mui/material';
import { green } from '@mui/material/colors';
import CustomCallIcon from './CallIcon';
import { Contact } from '../InviteDialog/InviteDialog';
import { useAppState } from '../../state';
import { apiGetContacts } from '../../utils/api';

const useStyles = makeStyles((theme: Theme) => {
  return {
    searchContainer: {
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'center',
    },
    inputField: {
      fontSize: '16px',
      width: '250px',
    },
    submitBtn: {
      marginLeft: '28px',
      marginBottom: '-6px',
    },
    root: {
      width: '100%',
      maxWidth: 360,
      backgroundColor: theme.palette.background.paper,
      marginTop: '12px',
    },
    listItemRoot: {
      cursor: 'auto',
      '&:hover': {
        background: '#fff',
      },
    },
    userList: {
      display: 'block',
    },
    mailList: {
      padding: 0,
      marginLeft: '14px',
    },
    mailListItem: {
      padding: '4px',
      marginTop: '6px',
    },
    phoneListItem: {
      cursor: 'pointer',
      transition: 'background .3s',
      '&:hover': {
        background: '#eee',
      },
    },
    callIcon: {
      color: green[500],
      fontSize: 30,
      marginRight: '6px',
    },
  };
});

interface VoiceContactListProps {
  handleCall: (toNumber: string) => void;
}

const VoiceContactList = ({ handleCall }: VoiceContactListProps) => {
  const classes = useStyles();
  const { username, domain, password } = useAppState();
  const searchBtn = useRef<HTMLButtonElement>(null);
  const [searchstring, setSearchstring] = useState<string>('');
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [contacts, setContacts] = useState<Contact[]>([]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearchstring(value);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.which === 13) {
      event.stopPropagation();
      !!searchBtn.current && searchBtn.current.focus();
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    if (isFetching || !searchstring) return;
    setIsFetching(true);
    apiGetContacts(password, username, domain, searchstring)
      .then(res => res.json())
      .then(data => {
        setSearchstring('');
        setContacts(data[0].concat(data[1]).filter((contact: Contact) => contact.firstname));
      })
      .catch(e => console.log(e))
      .finally(() => setIsFetching(false));
  };

  const handleNumberClick = (toNumber: string) => () => handleCall(toNumber);

  return (
    <div>
      <div className={classes.searchContainer}>
        <TextField
          id="standard-basic"
          label="Type name"
          onChange={handleSearchChange}
          onKeyPress={handleKeyPress}
          value={searchstring}
          className={classes.inputField}
        />
        <Button
          variant="contained"
          color="primary"
          className={classes.submitBtn}
          onClick={handleSubmit}
          disabled={!searchstring}
          ref={searchBtn}
        >
          {isFetching ? 'Searching...' : 'Search'}
        </Button>
      </div>
      {!!contacts.length && (
        <List className={classes.root}>
          {contacts.map(
            (contact: Contact) =>
              !!contact.phonenumbers.length && (
                <ListItem
                  className={classes.userList}
                  classes={{ root: classes.listItemRoot }}
                  key={contact.firstname + contact.lastname}
                  role={undefined}
                  dense
                >
                  <Typography variant="body1">
                    {contact.firstname} {contact.lastname ? contact.lastname : ''}
                  </Typography>
                  <List className={classes.mailList}>
                    {contact.phonenumbers &&
                      contact.phonenumbers.map((phone, index) => (
                        <ListItem
                          className={classes.mailListItem}
                          key={phone}
                          classes={{ root: classes.phoneListItem }}
                          onClick={handleNumberClick(phone.toString())}
                        >
                          <CustomCallIcon className={classes.callIcon} />
                          {/* style={{ color: green[500], fontSize: 30 }} */}
                          <Typography variant="body1">{phone}</Typography>
                        </ListItem>
                      ))}
                  </List>
                </ListItem>
              )
          )}
        </List>
      )}
    </div>
  );
};

export default VoiceContactList;
